import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { useAuth } from '@/stores/auth'

// Verbose request tracing — silent unless enabled in a dev browser:
//   localStorage.setItem('debugApi', '1')
const debugLog = (...args: unknown[]) => {
  if (import.meta.env.DEV && typeof localStorage !== 'undefined' && localStorage.getItem('debugApi')) console.log(...args)
}

// VERSION: 2025-11-11-FIX-LOCALHOST-PROXY-V3
// CRITICAL: Vite replaces import.meta.env.VITE_API_URL at build time
// If it's set to 'http://backend:8000', it gets baked into the bundle
// Solution: Always check window.location first and ignore VITE_API_URL in dev
const getApiBaseUrl = () => {
  // ALWAYS check window.location first - this runs at runtime, not build time
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname
    if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1') {
      // Force empty string to use Vite proxy, completely ignoring VITE_API_URL
      debugLog('[API Client V3] Running on localhost, FORCING empty baseURL for Vite proxy')
      return ''
    }
  }
  
  // Only use VITE_API_URL in production (non-localhost)
  // Note: This may still have 'http://backend:8000' baked in, but won't be used on localhost
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:47110'
  debugLog('[API Client V3] Production mode, using API URL:', apiUrl)
  return apiUrl
}

let API_BASE_URL = getApiBaseUrl()
debugLog('[API Client V3] MODULE LOADED - VERSION: 2025-11-11-FIX-LOCALHOST-PROXY-V3')
debugLog('[API Client V3] Initial API_BASE_URL:', API_BASE_URL || '(empty - using Vite proxy)')

// Create axios instance
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// CRITICAL FIX: Override baseURL after creation if on localhost
// This fixes the case where VITE_API_URL was set to 'http://backend:8000' at build time
if (typeof window !== 'undefined') {
  const hostname = window.location.hostname
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1') {
    debugLog('[API Client V3] Runtime override: Setting baseURL to empty for localhost')
    apiClient.defaults.baseURL = ''
    API_BASE_URL = ''
  }
}
debugLog('[API Client V3] Final API_BASE_URL after override:', apiClient.defaults.baseURL || '(empty - using Vite proxy)')

// Request interceptor - add JWT token and FORCE proxy in development
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    debugLog('[API Client Interceptor] Request interceptor called')
    debugLog('[API Client Interceptor] Original config.baseURL:', config.baseURL)
    debugLog('[API Client Interceptor] Original config.url:', config.url)
    
    // CRITICAL: Override baseURL at request time if on localhost
    // This ensures we use the proxy even if baseURL was set incorrectly
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname
      debugLog('[API Client Interceptor] window.location.hostname:', hostname)
      if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1') {
        // Force empty baseURL to use Vite proxy
        debugLog('[API Client Interceptor] On localhost - FORCING baseURL to empty')
        config.baseURL = ''
        // Also fix any URLs that might have backend:8000 in them
        if (config.url && config.url.includes('backend:8000')) {
          debugLog('[API Client Interceptor] Removing backend:8000 from URL:', config.url)
          config.url = config.url.replace(/https?:\/\/backend:8000/, '')
        }
        debugLog('[API Client Interceptor] After override - config.baseURL:', config.baseURL)
        debugLog('[API Client Interceptor] After override - config.url:', config.url)
      }
    }
    
    const auth = useAuth.getState()
    if (auth.accessToken) {
      config.headers.Authorization = `Bearer ${auth.accessToken}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor - handle 401 and token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true
      
      // Don't redirect for public endpoints (registration, login, etc.)
      const publicEndpoints = ['/api/v1/parents/validate-sharecode', '/api/v1/auth/login', '/api/v1/auth/reset-password', '/api/v1/contact']
      const isPublicEndpoint = publicEndpoints.some(endpoint => originalRequest.url?.includes(endpoint))
      
      if (isPublicEndpoint) {
        // For public endpoints, just reject the error without redirecting
        return Promise.reject(error)
      }
      
      const auth = useAuth.getState()
      if (auth.refreshToken) {
        try {
          // Try to refresh token
          const refreshUrl = API_BASE_URL 
            ? `${API_BASE_URL}/api/v1/auth/refresh`
            : '/api/v1/auth/refresh'
          const response = await axios.post(refreshUrl, {
            refresh_token: auth.refreshToken,
          })
          
          const { access_token } = response.data
          useAuth.getState().login(auth.user!, access_token, auth.refreshToken)
          
          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${access_token}`
          return apiClient(originalRequest)
        } catch (refreshError) {
          // Refresh failed - logout user only if we're on a protected route
          const currentPath = window.location.pathname
          const publicRoutes = ['/login', '/register', '/reset-password', '/subscription', '/contact', '/privacy']
          if (!publicRoutes.includes(currentPath)) {
            useAuth.getState().logout()
            window.location.href = '/login'
          }
          return Promise.reject(refreshError)
        }
      } else {
        // No refresh token - logout only if on protected route
        const currentPath = window.location.pathname
        const publicRoutes = ['/login', '/register', '/reset-password', '/subscription', '/contact', '/privacy']
        if (!publicRoutes.includes(currentPath)) {
          useAuth.getState().logout()
          window.location.href = '/login'
        }
      }
    }
    
    const detail = (error.response?.data as any)?.detail
    if (error.response?.status === 403 && typeof detail === 'string' && detail.includes('cannot be accessed at this time')) {
      sessionStorage.setItem('parishBlocked', detail)
      useAuth.getState().logout()
      if (window.location.pathname !== '/login') window.location.href = '/login'
    }

    return Promise.reject(error)
  }
)

export default apiClient
